import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import "./admin-review.css";

type JsonRequest = <T>(path: string, init?: RequestInit) => Promise<T>;

const defaultRequest: JsonRequest = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: init?.body ? { "content-type": "application/json", ...init.headers } : init?.headers,
  });
  const payload = await response.json().catch(() => ({ error: "応答を読み取れませんでした" })) as T & { error?: string };
  if (!response.ok) throw new Error(payload.error || "処理に失敗しました");
  return payload;
};

type ReportStatus = "pending" | "reviewing" | "resolved" | "dismissed";
type Report = {
  id: string;
  reporterUserId: string | null;
  reportedUserId: string | null;
  targetType: "user" | "question" | "answer";
  targetId: string;
  reason: string;
  details: string;
  snapshot: Record<string, unknown>;
  status: ReportStatus;
  currentState: string | null;
  reporterName: string;
  reportedName: string;
  createdAt: number;
  updatedAt: number;
  resolvedAt: number | null;
  resolutionNote: string;
};

type AuditLog = {
  id: string;
  actorName: string | null;
  actorRole: string;
  actionType: string;
  targetType: string;
  targetId: string | null;
  metadata: Record<string, unknown>;
  createdAt: number;
};

const REASON_LABELS: Record<string, string> = {
  harassment: "嫌がらせ・いじめ",
  hate: "差別・ヘイト",
  violence: "暴力・脅迫",
  sexual: "性的な内容",
  self_harm: "自傷・自殺に関する危険",
  misinformation: "誤解を招く情報",
  spam: "スパム・宣伝",
  privacy: "個人情報・プライバシー",
  impersonation: "なりすまし",
  other: "その他",
};

const STATUS_LABELS: Record<ReportStatus | "all", string> = {
  pending: "未確認",
  reviewing: "審査中",
  resolved: "対応済み",
  dismissed: "見送り",
  all: "すべて",
};

function time(value: number | null): string {
  return value ? new Intl.DateTimeFormat("ja-JP", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—";
}

function snapshotText(report: Report): string {
  const snapshot = report.snapshot;
  const primary = snapshot.body || snapshot.reason || snapshot.displayName || snapshot.id;
  return typeof primary === "string" ? primary : JSON.stringify(snapshot);
}

function moderationAction(report: Report): "hide_question" | "hide_answer" | "ban_user" {
  if (report.targetType === "question") return "hide_question";
  if (report.targetType === "answer") return "hide_answer";
  return "ban_user";
}

function moderationLabel(action: ReturnType<typeof moderationAction>): string {
  if (action === "hide_question" || action === "hide_answer") return "対象を非表示にして解決";
  return "ユーザーをBANして解決";
}

function restoreAction(report: Report): "unhide_question" | "unhide_answer" | "unban_user" | null {
  if (report.targetType === "question" && report.currentState === "hidden") return "unhide_question";
  if (report.targetType === "answer" && report.currentState === "hidden") return "unhide_answer";
  if (report.targetType === "user" && report.currentState === "banned") return "unban_user";
  return null;
}

export function AdminClaimPanel({ request = defaultRequest, onClaimed }: { request?: JsonRequest; onClaimed?: () => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      await request("/api/admin/claim", { method: "POST", body: JSON.stringify({ code }) });
      setCode("");
      setMessage("管理者権限が有効になりました。");
      onClaimed?.();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "管理者権限を有効にできませんでした");
    } finally {
      setBusy(false);
    }
  }

  return <section className="admin-claim-card" aria-labelledby="admin-claim-title">
    <span className="admin-kicker">STAGING ONLY</span>
    <h2 id="admin-claim-title">管理者権限</h2>
    <p>運用担当者だけに共有されたテスト環境用コードを入力してください。</p>
    <form onSubmit={submit}>
      <label htmlFor="admin-claim-code">管理者コード</label>
      <div className="admin-inline-form">
        <input id="admin-claim-code" type="password" autoComplete="off" value={code} onChange={(event) => setCode(event.target.value)} required maxLength={256} disabled={busy} />
        <button type="submit" disabled={busy || !code}>{busy ? "確認中…" : "有効にする"}</button>
      </div>
      {message && <p className="admin-form-message" role="status">{message}</p>}
    </form>
  </section>;
}

export function AdminReview({ request = defaultRequest, onClose }: { request?: JsonRequest; onClose?: () => void }) {
  const [status, setStatus] = useState<ReportStatus | "all">("pending");
  const [reports, setReports] = useState<Report[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [featured, setFeatured] = useState<{ enabled: boolean; label: string; questionId: string | null }>({ enabled: false, label: "注目の問い", questionId: null });
  const [featuredQuestions, setFeaturedQuestions] = useState<Array<{ id: string; body: string; authorName: string }>>([]);
  const [featuredBusy, setFeaturedBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [reportData, auditData, featuredData] = await Promise.all([
        request<{ reports: Report[] }>(`/api/admin/reports?status=${status}`),
        request<{ auditLogs: AuditLog[] }>("/api/admin/audit?limit=40"),
        request<{ featured: typeof featured; questions: typeof featuredQuestions }>("/api/admin/featured"),
      ]);
      setReports(reportData.reports);
      setAuditLogs(auditData.auditLogs);
      setFeatured(featuredData.featured);
      setFeaturedQuestions(featuredData.questions);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "管理データを取得できませんでした");
    } finally {
      setLoading(false);
    }
  }, [request, status]);

  async function saveFeatured(event: FormEvent) {
    event.preventDefault();
    setFeaturedBusy(true);
    setError("");
    try {
      const data = await request<{ featured: typeof featured }>("/api/admin/featured", {
        method: "PUT", body: JSON.stringify(featured),
      });
      setFeatured(data.featured);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "注目枠を更新できませんでした");
    } finally {
      setFeaturedBusy(false);
    }
  }

  useEffect(() => { void load(); }, [load]);

  async function changeStatus(report: Report, nextStatus: "reviewing" | "dismissed") {
    const note = (notes[report.id] || "").trim();
    if (note.length < 3) {
      setError("審査メモを3文字以上で入力してください。");
      return;
    }
    setBusyId(report.id);
    setError("");
    try {
      await request(`/api/admin/reports/${encodeURIComponent(report.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus, note }),
      });
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "通報状態を更新できませんでした");
    } finally {
      setBusyId("");
    }
  }

  async function applyModeration(
    report: Report,
    action: ReturnType<typeof moderationAction> | NonNullable<ReturnType<typeof restoreAction>> = moderationAction(report),
    resolveReport = true,
  ) {
    const reason = (notes[report.id] || "").trim();
    if (reason.length < 3) {
      setError("判断理由を3文字以上で入力してください。");
      return;
    }
    setBusyId(report.id);
    setError("");
    try {
      await request("/api/admin/moderation", {
        method: "POST",
        body: JSON.stringify({
          action,
          targetType: report.targetType,
          targetId: report.targetId,
          reason,
          ...(resolveReport ? { reportId: report.id } : {}),
        }),
      });
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "管理操作を完了できませんでした");
    } finally {
      setBusyId("");
    }
  }

  const unresolved = useMemo(() => reports.filter((report) => report.status === "pending" || report.status === "reviewing").length, [reports]);

  return <main className="admin-shell">
    <header className="admin-header">
      <div>
        <span className="admin-kicker">SAFETY REVIEW</span>
        <h1>安全管理</h1>
        <p>通報、対応、監査証跡をひとつの画面で確認します。</p>
      </div>
      {onClose && <button type="button" className="admin-ghost" onClick={onClose}>閉じる</button>}
    </header>

    <section className="admin-overview" aria-label="審査概要">
      <div><strong>{unresolved}</strong><span>現在の要対応</span></div>
      <div><strong>{reports.length}</strong><span>表示中の通報</span></div>
      <div><strong>{auditLogs.length}</strong><span>最新の監査証跡</span></div>
    </section>

    <nav className="admin-filter" aria-label="通報状態">
      {(Object.keys(STATUS_LABELS) as Array<ReportStatus | "all">).map((key) => <button
        type="button" key={key} aria-current={status === key ? "page" : undefined}
        onClick={() => setStatus(key)}>{STATUS_LABELS[key]}</button>)}
    </nav>

    <section className="admin-featured" aria-labelledby="admin-featured-title">
      <div><span className="admin-kicker">FEATURED SLOT</span><h2 id="admin-featured-title">注目の問い</h2><p>「答える」の先頭に表示する問いを運営側で選びます。</p></div>
      <form onSubmit={(event) => void saveFeatured(event)}>
        <label><input type="checkbox" checked={featured.enabled} onChange={(event) => setFeatured((current) => ({ ...current, enabled: event.target.checked }))} /> 注目枠を表示する</label>
        <label>見出し<input value={featured.label} maxLength={40} onChange={(event) => setFeatured((current) => ({ ...current, label: event.target.value }))} required /></label>
        <label>問い<select value={featured.questionId || ""} onChange={(event) => setFeatured((current) => ({ ...current, questionId: event.target.value || null }))} required={featured.enabled}><option value="">選択しない</option>{featuredQuestions.map((question) => <option key={question.id} value={question.id}>{question.body} — {question.authorName}</option>)}</select></label>
        <button type="submit" disabled={featuredBusy || (featured.enabled && !featured.questionId)}>{featuredBusy ? "保存中…" : "注目枠を保存"}</button>
      </form>
    </section>

    {error && <p className="admin-error" role="alert">{error}</p>}
    {loading ? <p className="admin-empty">読み込み中…</p> : reports.length === 0 ? <p className="admin-empty">この状態の通報はありません。</p> : <section className="admin-report-list" aria-label="通報一覧">
      {reports.map((report) => {
        const action = moderationAction(report);
        const restore = restoreAction(report);
        const busy = busyId === report.id;
        return <article className="admin-report-card" key={report.id}>
          <header>
            <div className="admin-report-meta">
              <span className={`admin-status admin-status-${report.status}`}>{STATUS_LABELS[report.status]}</span>
              <span>{REASON_LABELS[report.reason] || report.reason}</span>
              <time dateTime={new Date(report.createdAt).toISOString()}>{time(report.createdAt)}</time>
            </div>
            <span className="admin-target-chip">{report.targetType.toUpperCase()}</span>
          </header>
          <h2>{snapshotText(report)}</h2>
          <dl>
            <div><dt>通報者</dt><dd>{report.reporterName}</dd></div>
            <div><dt>対象ユーザー</dt><dd>{report.reportedName}</dd></div>
            <div><dt>対象ID</dt><dd><code>{report.targetId}</code></dd></div>
          </dl>
          {report.details && <blockquote>{report.details}</blockquote>}
          {report.resolutionNote && <p className="admin-resolution"><strong>審査メモ：</strong>{report.resolutionNote}</p>}
          {(report.status === "pending" || report.status === "reviewing") && <div className="admin-review-controls">
            <label htmlFor={`note-${report.id}`}>審査メモ / 判断理由</label>
            <textarea id={`note-${report.id}`} rows={3} maxLength={1000} value={notes[report.id] || ""}
              onChange={(event) => setNotes((current) => ({ ...current, [report.id]: event.target.value }))}
              placeholder="判断根拠を第三者が追跡できる形で残します。" disabled={busy} />
            <div className="admin-actions">
              {report.status === "pending" && <button type="button" className="admin-secondary" disabled={busy} onClick={() => void changeStatus(report, "reviewing")}>審査を開始</button>}
              <button type="button" className="admin-danger" disabled={busy} onClick={() => void applyModeration(report)}>{busy ? "処理中…" : moderationLabel(action)}</button>
              <button type="button" className="admin-ghost" disabled={busy} onClick={() => void changeStatus(report, "dismissed")}>見送り</button>
            </div>
          </div>}
          {report.status !== "pending" && report.status !== "reviewing" && restore && <div className="admin-review-controls admin-restore-controls">
            <label htmlFor={`restore-note-${report.id}`}>復旧理由</label>
            <textarea id={`restore-note-${report.id}`} rows={2} maxLength={1000} value={notes[report.id] || ""}
              onChange={(event) => setNotes((current) => ({ ...current, [report.id]: event.target.value }))}
              placeholder="判断の変更理由を監査証跡に残します。" disabled={busy} />
            <button type="button" className="admin-secondary" disabled={busy}
              onClick={() => void applyModeration(report, restore, false)}>
              {busy ? "処理中…" : report.targetType === "user" ? "BANを解除" : "対象を再表示"}
            </button>
          </div>}
        </article>;
      })}
    </section>}

    <section className="admin-audit" aria-labelledby="admin-audit-title">
      <header><div><span className="admin-kicker">IMMUTABLE LOG</span><h2 id="admin-audit-title">監査証跡</h2></div><button type="button" className="admin-ghost" onClick={() => void load()}>再読み込み</button></header>
      <div className="admin-audit-scroll">
        <table>
          <thead><tr><th>時刻</th><th>操作</th><th>対象</th><th>実行者</th></tr></thead>
          <tbody>{auditLogs.map((entry) => <tr key={entry.id}>
            <td>{time(entry.createdAt)}</td>
            <td><code>{entry.actionType}</code></td>
            <td>{entry.targetType}<br /><small>{entry.targetId || "—"}</small></td>
            <td>{entry.actorName || "削除済み / SYSTEM"}<br /><small>{entry.actorRole}</small></td>
          </tr>)}</tbody>
        </table>
      </div>
    </section>
  </main>;
}

type NotificationItem = {
  id: string;
  kind: "security" | "transaction";
  title: string;
  body: string;
  readAt: number | null;
  createdAt: number;
};

export function NotificationInbox({ request = defaultRequest }: { request?: JsonRequest }) {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await request<{ items: NotificationItem[]; unreadCount: number }>("/api/notifications?limit=50");
      setItems(data.items);
      setUnreadCount(data.unreadCount);
      setError("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "通知を取得できませんでした");
    }
  }, [request]);

  useEffect(() => { void load(); }, [load]);

  async function markRead(id: string) {
    try {
      await request(`/api/notifications/${encodeURIComponent(id)}/read`, { method: "PATCH" });
      setItems((current) => current.map((item) => item.id === id && !item.readAt ? { ...item, readAt: Date.now() } : item));
      setUnreadCount((current) => Math.max(0, current - 1));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "通知を既読にできませんでした");
    }
  }

  async function markAll() {
    try {
      await request("/api/notifications/read-all", { method: "PATCH" });
      const now = Date.now();
      setItems((current) => current.map((item) => ({ ...item, readAt: item.readAt || now })));
      setUnreadCount(0);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "通知を既読にできませんでした");
    }
  }

  return <section className="notification-inbox" aria-labelledby="notification-title">
    <header><div><span className="admin-kicker">INBOX</span><h2 id="notification-title">お知らせ {unreadCount > 0 && <b>{unreadCount}</b>}</h2></div>{unreadCount > 0 && <button type="button" className="admin-ghost" onClick={() => void markAll()}>すべて既読</button>}</header>
    {error && <p className="admin-error" role="alert">{error}</p>}
    {items.length === 0 ? <p className="admin-empty">新しいお知らせはありません。</p> : <ul>{items.map((item) => <li key={item.id} className={item.readAt ? "is-read" : "is-unread"}>
      <button type="button" onClick={() => !item.readAt && void markRead(item.id)}>
        <span className={`notification-kind notification-kind-${item.kind}`}>{item.kind === "security" ? "安全" : "お知らせ"}</span>
        <strong>{item.title}</strong>
        <span>{item.body}</span>
        <time dateTime={new Date(item.createdAt).toISOString()}>{time(item.createdAt)}</time>
      </button>
    </li>)}</ul>}
  </section>;
}
